import {DataStatus, Form, FormField} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useGetManufacturersQuery} from '@dms/api/core';
import {useGetSuppliersQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {getOptionsFromManufacturers, getOptionsFromSuppliers} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {FormToDataGridConnectorProps} from 'features/eag-grid';

type MaterialWarehouseTabExternalFiltersForm = Partial<{
  isAvailable: boolean;
  supplierId: string;
  isInStock: boolean;
  catalogNumber: string;
  manufacturerId: string;
}>;

type WarehouseTabExternalFiltersProps = FormToDataGridConnectorProps & RequiredTestIdProps;

export function MaterialWarehouseTabExternalFilters(props: WarehouseTabExternalFiltersProps) {
  const {
    data: manufacturers,
    isLoading: isManufacturersLoading,
    isError: isManufacturersError,
  } = useGetManufacturersQuery();

  const {
    data: suppliers,
    isLoading: isSuppliersLoading,
    isError: isSuppliersError,
  } = useGetSuppliersQuery();

  const isLoading = isManufacturersLoading || isSuppliersLoading;
  const isError = isManufacturersError || isSuppliersError;

  const formValues: MaterialWarehouseTabExternalFiltersForm = {
    isAvailable: false,
    isInStock: false,
    ...props.values,
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Form<MaterialWarehouseTabExternalFiltersForm>
        values={formValues}
        defaultValues={{}}
        onChange={props.onChange}
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              name="catalogNumber"
              type="text"
              label={i18n.t('entity.warehouse.labels.catalogueNumber')}
              data-testid={suffixTestId('inputs.catalogueNumber', props)}
            />
            <FormField
              control={control}
              name="manufacturerId"
              type="multiChoice"
              label={i18n.t('entity.warehouse.labels.manufacturer')}
              options={getOptionsFromManufacturers(manufacturers)}
              data-testid={suffixTestId('inputs.manufacturer', props)}
            />
            <FormField
              control={control}
              name="supplierId"
              type="multiChoice"
              label={i18n.t('entity.warehouse.labels.articleDefaultSupplier')}
              options={getOptionsFromSuppliers(suppliers)}
              data-testid={suffixTestId('inputs.supplierId', props)}
            />
            <FormField
              control={control}
              name="isAvailable"
              label={i18n.t('entity.warehouse.labels.availableOnly')}
              type="checkbox"
              data-testid={suffixTestId('availableOnly', props)}
            />
            <FormField
              control={control}
              name="isInStock"
              label={i18n.t('entity.warehouse.labels.inStock')}
              type="checkbox"
              data-testid={suffixTestId('inStock', props)}
            />
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
